import { Analysis, PossibleRootCauseCategory, ProbabilityCategory } from '.';
import { ADRENAL_GAUGE_IDS } from './adrenal-gauge-ids.generated';
import {
  deviationDirection,
  deviationRank,
  isPastMidpoint,
  probabilityCategory,
} from './gaugeSeverity';

/**
 * Stress & Adrenal Balance analysis.
 *
 * First cut (MVP): a single-marker interpretation per marker, mirroring the
 * Electrolytes/Hormones style, plus the adrenal-axis gauge. The markers
 * themselves are MOVED here (display + interpretation):
 *   • Na/K ratio        — from Electrolytes (was 9032/9034)
 *   • Cortisol AM/PM    — from Hormones (was 15000/15010 / 15020/15030)
 *   • DHEA-S            — from Hormones (was 15040/15050)
 *   • Pregnenolone      — from Hormones (was 15220/15230)
 *
 * Possible root-cause CONTENT lives in the FurtherConsiderations DB keyed to the
 * 90xxx categoryIds (same as the thyroid pattern categories), to be populated
 * later. A full physiology-area pattern set (like thyroid's) is a planned
 * follow-up that drops onto this scaffold.
 */

/**
 * Adrenal-axis gauge driven by the Na/K ratio, rendered on the SAME standard
 * 0–100% sweep the Glucose / Thyroid gauges use (LabsmartsGuage).
 *
 * Model — essential FUNCTION first, then RESERVE. All four hormones are made by
 * the adrenals; the split is what a low value MEANS, not where it's made:
 *   • FUNCTION = cortisol + aldosterone (read via the Na/K ratio) — the hormones
 *     the adrenals defend first, so a fall means the gland can't keep up. Na/K is
 *     primary: below optimal → UNDERACTIVITY, above → OVERACTIVITY. When Na/K
 *     reads optimal, cortisol sets the direction.
 *   • RESERVE = DHEA-S + pregnenolone (the buffer + raw material) — sacrificed
 *     before function slips, so they signal earlier. They only steer the gauge
 *     once function reads optimal: low → REDUCED STRESS RESILIENCE (gauge label;
 *     internal condition key stays 'reserve'), high →
 *     OVERACTIVITY (over-producing androgens / precursors).
 *   • Everything optimal → BALANCED (adrenal imbalance Less Likely).
 * Once the direction is fixed, every same-direction marker contributes; opposite-
 * direction markers are ignored, so the panel always reads a single condition.
 * (Naming note: "insufficiency / excess" were retired — they're diagnostic terms,
 * and reserve depletion with intact cortisol/aldosterone is not true insufficiency.)
 *
 * Severity — a Glucose-style hybrid over the same-direction markers:
 *   • all within lab (past optimal): graded by the lab↔optimal midpoint — a
 *     single marker reads 55 (mild) / 60 (past midpoint); two-or-more read
 *     65 / 72 / 80 by how many are past midpoint (Possible → Likely). 55, not 50,
 *     so a mild single needle clears the green/yellow (Less Likely/Possible) line.
 *   • any out of lab (past the lab bound): count-stacks — one 85 (95 if that one
 *     is past alarm), two 97, three-or-more 99 (Highly Likely).
 * Colour bands: green < 50 · yellow 50–70 · orange 70–90 · red ≥ 90.
 */
export type AdrenalCondition =
  | 'underactivity'
  | 'overactivity'
  | 'reserve'
  | 'dominance'
  | 'balanced';

/** A direction arrow shown on the condition line beneath the gauge. */
export interface AdrenalMarkerArrow {
  direction: 'up' | 'down';
  label: string;
}

export interface AdrenalGauge {
  /** 0–100 needle position for the standard sweep gauge (LabsmartsGuage). */
  probability: number;
  category: ProbabilityCategory;
  /** Direction chosen by the Na/K ratio. */
  condition: AdrenalCondition;
  /** Raw Na/K ratio value, or undefined when the electrolytes were not run.
   *  (The panel shows the marker's displayValue; this is informational.) */
  ratioValue?: number;
  /** Na/K + any same-direction escalators, for the arrows on the line below. */
  markers: AdrenalMarkerArrow[];
  /**
   * The Adrenal Gauge tab row (cat 904xx) that explains this reading, or
   * undefined when the sheet has no row for the state. Pushed into the
   * Interpretation tab so a practitioner can see WHY the needle sits where it
   * does. Undefined is survivable — the gauge still renders and only the
   * explanation is missing — so a sheet edit degrades rather than crashes.
   */
  gaugeCategoryId?: number;
}

// deviationRank / isPastMidpoint / probabilityCategory / deviationDirection live in
// gaugeSeverity.ts (shared with the Iron gauge since 2026-09-20); bodies unchanged.

/**
 * Direction the escalator hormones point when Na/K is silent. The strongest
 * deviation across cortisol AM/PM, DHEA-S and pregnenolone wins; ties break
 * toward the higher-priority marker (cortisol AM, then PM, then DHEA-S, then
 * pregnenolone). Null when none of them deviate.
 */
function hormoneDirection(markers: any[]): 'low' | 'high' | null {
  let bestRank = 0;
  let dir: 'low' | 'high' | null = null;
  for (const m of markers) {
    const low = deviationRank(m, 'low');
    const high = deviationRank(m, 'high');
    if (low != null && low > bestRank) {
      bestRank = low;
      dir = 'low';
    } else if (high != null && high > bestRank) {
      bestRank = high;
      dir = 'high';
    }
  }
  return dir;
}

/**
 * Map the adrenal markers to the standard-sweep gauge state. Pure + null-safe so
 * it can be unit-tested and reused by the insights component. Cortisol sets the
 * direction when it deviates; Na/K fills in only when cortisol is silent. Na/K is
 * still required to render at all (it supplies ratioValue), so this returns null
 * when Na/K is unmeasured or range-less.
 */
export function adrenalGauge(
  naK?: any,
  escalators: { cortisolAM?: any; cortisolPM?: any; dheaS?: any; pregnenolone?: any } = {},
): AdrenalGauge | null {
  const { cortisolAM, cortisolPM, dheaS, pregnenolone } = escalators;

  // Na/K is OPTIONAL. A panel carrying cortisol and DHEA-S but no electrolytes still
  // describes the adrenal axis — better than the ratio does — so requiring Na/K here
  // suppressed the whole gauge for those clients. Render whenever ANY of the four
  // markers is usable; bail only when none is.
  const usable = (m?: any) => !!m?.hasValue?.() && !!m.range;
  const hasNaK = usable(naK);
  if (!hasNaK && !usable(cortisolAM) && !usable(cortisolPM) && !usable(dheaS) && !usable(pregnenolone)) {
    return null;
  }
  const v: number | undefined = hasNaK ? naK.value : undefined;

  // ── Direction ──────────────────────────────────────────────────────────────
  // Cortisol and aldosterone (read via the Na/K ratio) are the essential-function
  // hormones the adrenals defend first — they decide under- vs over-activity.
  //
  // CORTISOL LEADS. Serum Na/K is only a weak aldosterone proxy: confirmed
  // subnormal ACTH-stimulated aldosterone occurs with entirely normal serum sodium
  // and potassium (el-Deiry, JCEM 1997;82:856-60, PMID 9062496), and the ratio also
  // moves for reasons that are not adrenal at all — potassium-sparing drugs, CKD,
  // acidosis, cell breakdown, or dilutional hyponatremia in SIADH / heart failure /
  // cirrhosis. So Na/K fills in only when cortisol is silent (unmeasured or
  // optimal), and when Na/K is the ONLY deviating marker it cannot carry the gauge
  // past Possible (see NAK_ONLY_MAX below).
  //
  // Before 2026-08-23 Na/K was checked FIRST and unconditionally, which meant a
  // potassium supplement could score "Adrenal Underactivity is Likely (85%)" on top
  // of a perfectly normal cortisol and DHEA-S — and, worse, could report
  // underactivity while the pattern box directly beneath it showed a HIGH cortisol.
  //
  // DHEA-S and pregnenolone are the RESERVE / raw-material hormones, sacrificed
  // before function slips, so they only steer the gauge once function reads optimal
  // — low → Reduced Stress Resilience (gauge label; internal condition key stays
  // 'reserve'; reserve depleted while function still holds), high → over-activity
  // (over-producing androgens / precursors).
  const nakDir: 'low' | 'high' | null = hasNaK
    ? naK.value < naK.range.optimalMin
      ? 'low'
      : naK.value > naK.range.optimalMax
        ? 'high'
        : null
    : null;
  const cortisolDir = hormoneDirection([cortisolAM, cortisolPM]);
  const functionDir: 'low' | 'high' | null = cortisolDir !== null ? cortisolDir : nakDir;

  // CORTISOL DOMINANCE — high output alongside low reserve. Added 2026-08-31.
  // Before this the gauge only ever tallied markers pointing the SAME way as the
  // direction it picked, so a high cortisol with depleted DHEA-S and pregnenolone
  // scored identically to a high cortisol with perfect reserve: 85% "Adrenal
  // Overactivity", marker row "▲Cortisol", the two low markers invisible. That is
  // a materially different client, and the pattern layer already distinguishes it
  // (the 90240/90270/90360-90375 Cortisol-Dominant family), so the gauge now does
  // too. Deliberately says nothing about cause or mechanism: "dominance" names the
  // measured relationship between cortisol and reserve and stops there.
  const reserveLow =
    deviationRank(dheaS, 'low') != null || deviationRank(pregnenolone, 'low') != null;
  const isDominance = cortisolDir === 'high' && reserveLow;

  let dir: 'low' | 'high' | null = functionDir;
  let condition: AdrenalCondition;
  if (isDominance) {
    condition = 'dominance';
    dir = 'high'; // cortisol's direction; the tally below is mixed-direction
  } else if (functionDir) {
    condition = functionDir === 'low' ? 'underactivity' : 'overactivity';
  } else {
    dir = hormoneDirection([dheaS, pregnenolone]);
    condition = dir === 'high' ? 'overactivity' : 'reserve';
  }

  // Nothing deviates → balanced, adrenal imbalance Less Likely. The needle then
  // scales with how much evidence was actually gathered, mirroring the Glucose
  // gauge's confidence tier (5 / 15 / 25): Cortisol and DHEA-S read the axis most
  // directly, so a panel carrying both reads most confident; Cortisol on its own is
  // the middle rung; Na/K alone (or with pregnenolone only) stays at 25.
  // Pregnenolone is a precursor, so like TG/HDL in the glucose tier it never lifts
  // the confidence on its own.
  if (dir === null) {
    const hasCortisol = usable(cortisolAM) || usable(cortisolPM);
    const probability = hasCortisol && usable(dheaS) ? 5 : hasCortisol ? 15 : 25;
    return {
      probability,
      category: probabilityCategory(probability),
      condition: 'balanced',
      ratioValue: v,
      markers: [],
      gaugeCategoryId: ADRENAL_GAUGE_IDS[`balanced|${probability}`],
    };
  }

  // Tally the same-direction markers. Na/K leads the row; cortisol AM/PM collapse
  // to a single "Cortisol" (the worse-ranked of the two). Each marker is either
  // out of lab (rank 2/3) or within lab past optimal (rank 1, graded by midpoint).
  const markers: AdrenalMarkerArrow[] = [];
  let nOutOfLab = 0;         // markers past the lab bound
  let worstOutOfLabRank = 0; // 3 when the sole out-of-lab marker is past alarm
  let nWithinLab = 0;        // markers past optimal but within lab
  let nWithinLabPastMid = 0; // ...of which, past the lab↔optimal midpoint

  // In the dominance state the finding IS the disagreement, so every deviating
  // marker counts whichever way it points; elsewhere only same-direction markers do.
  const consider = (marker: any, label: string) => {
    let markerDir: 'low' | 'high' | null = dir;
    let r = deviationRank(marker, dir as 'low' | 'high');
    if (r == null && condition === 'dominance') {
      const opposite = dir === 'high' ? 'low' : 'high';
      r = deviationRank(marker, opposite);
      if (r != null) markerDir = opposite;
    }
    if (r == null) return;
    markers.push({ direction: markerDir === 'low' ? 'down' : 'up', label });
    if (r >= 2) {
      nOutOfLab += 1;
      worstOutOfLabRank = Math.max(worstOutOfLabRank, r);
    } else {
      nWithinLab += 1;
      if (isPastMidpoint(marker, markerDir as 'low' | 'high')) nWithinLabPastMid += 1;
    }
  };

  // Cortisol AM / PM → one "Cortisol" marker (the worse-deviating of the two).
  // Rank alone can't separate two within-lab cortisols, so the midpoint breaks
  // the tie: past the lab↔optimal midpoint beats merely mild at the same rank.
  // Without it, adding a second, milder cortisol would LOWER the gauge (the
  // mild one wins the tie and grades the pair down, 60 to 55).
  const cortisolSeverity = (m: any): number => {
    const r = deviationRank(m, dir as 'low' | 'high');
    if (r == null) return -1;
    return r * 2 + (isPastMidpoint(m, dir as 'low' | 'high') ? 1 : 0);
  };
  const cortisol =
    cortisolSeverity(cortisolAM) >= cortisolSeverity(cortisolPM) ? cortisolAM : cortisolPM;

  consider(naK, 'Na/K');
  consider(cortisol, 'Cortisol');
  consider(dheaS, 'DHEA-S');
  consider(pregnenolone, 'Pregnenolone');

  // Glucose-style hybrid (see the header note): out-of-lab markers count-stack;
  // an all-within-lab read is graded by the lab↔optimal midpoint.
  let probability: number;
  if (nOutOfLab >= 1) {
    if (nOutOfLab === 1) probability = worstOutOfLabRank >= 3 ? 95 : 85;
    else if (nOutOfLab === 2) probability = 97;
    else probability = 99;
  } else if (nWithinLab === 1) {
    probability = nWithinLabPastMid >= 1 ? 60 : 55;
  } else {
    probability = nWithinLabPastMid >= 2 ? 80 : nWithinLabPastMid === 1 ? 72 : 65;
  }

  // Na/K on its own is weak evidence (see the Direction note): it moves for
  // non-adrenal reasons and stays normal in confirmed mineralocorticoid
  // insufficiency. When it is the ONLY marker in the row it reports as supporting
  // evidence rather than a verdict, capped below the Likely band — the same
  // downgrade the Immune and Anemia gauges apply to their weaker reads.
  const NAK_ONLY_MAX = 68;
  if (markers.length === 1 && markers[0].label === 'Na/K') {
    probability = Math.min(probability, NAK_ONLY_MAX);
  }

  // Severity descriptor, matching the wording the Adrenal Gauge tab uses. These
  // strings are load-bearing: sync-adrenal-gauge-ids.js parses the same words out
  // of the sheet's Logic column, so changing one side without the other makes the
  // lookup miss (gaugeCategoryId goes undefined) rather than mis-resolve.
  const severity =
    nOutOfLab === 0
      ? nWithinLabPastMid === 0
        ? 'all mild'
        : nWithinLabPastMid === 1
          ? 'one past mid'
          : 'two or more past mid'
      : nOutOfLab === 1
        ? worstOutOfLabRank >= 3
          ? 'one past alarm bound'
          : 'one past lab range'
        : nOutOfLab === 2
          ? 'two past lab range'
          : 'three or more past lab range';
  // Dominance is mixed-direction, so its signature carries an arrow per marker;
  // the single-direction conditions keep the original `condition|dir|markers` form.
  const signature =
    condition === 'dominance'
      ? `dominance|${markers.map(m => (m.direction === 'up' ? '▲' : '▼') + m.label).join(',')}|${severity}`
      : `${condition}|${dir}|${markers.map(m => m.label).join(',')}|${severity}`;

  return {
    probability,
    category: probabilityCategory(probability),
    condition,
    ratioValue: v,
    markers,
    gaugeCategoryId: ADRENAL_GAUGE_IDS[signature],
  };
}

/** A pattern that fires for the current markers: its content categoryId plus the
 *  marker arrows to show in its box. */
export interface AdrenalPattern {
  categoryId: number;
  markers: AdrenalMarkerArrow[];
  /**
   * For a combination pattern, the single-marker categoryIds it stands in for.
   * The Insights panel ignores this (it renders the one combination box), but
   * the Possible Root Causes and References tabs need it: root causes are
   * authored PER MARKER, and no combination id has Causes rows, so without this
   * a client matching any defined combination sees an empty tab. Absent on a
   * single-marker pattern, which already IS its own category.
   */
  covers?: number[];
}

// Cortisol AM/PM collapse to one "Cortisol"; the worse-ranked time-point sets the
// direction and which single-marker content id applies. Ties go to AM (primary).
function pickWorseCortisol(am: any, pm: any): { dir: 'high' | 'low'; single: number } | null {
  const rankOf = (m: any): { rank: number; dir: 'high' | 'low' } | null => {
    const low = deviationRank(m, 'low');
    if (low != null) return { rank: low, dir: 'low' };
    const high = deviationRank(m, 'high');
    if (high != null) return { rank: high, dir: 'high' };
    return null;
  };
  const a = rankOf(am);
  const p = rankOf(pm);
  if (!a && !p) return null;
  const useAM = !!a && (!p || a.rank >= p.rank);
  const chosen = useAM ? a! : p!;
  const single = useAM
    ? chosen.dir === 'high' ? 90015 : 90020
    : chosen.dir === 'high' ? 90025 : 90030;
  return { dir: chosen.dir, single };
}

// Defined combination patterns — exact marker+direction sets (see the Adrenal
// Patterns tab). A deviation set must match one exactly to fire as a combo.
export const ADRENAL_COMBOS: Array<{ id: number; set: { [key: string]: 'high' | 'low' } }> = [
  { id: 90240, set: { cortisol: 'high', dheaS: 'low' } },
  { id: 90245, set: { cortisol: 'low', dheaS: 'low' } },
  { id: 90250, set: { naK: 'low', cortisol: 'low' } },
  { id: 90255, set: { naK: 'high', cortisol: 'high' } },
  { id: 90260, set: { pregnenolone: 'low', dheaS: 'low' } },
  { id: 90265, set: { cortisol: 'high', dheaS: 'high' } },
  { id: 90270, set: { cortisol: 'high', dheaS: 'low', pregnenolone: 'low' } },
  { id: 90275, set: { cortisol: 'low', dheaS: 'low', pregnenolone: 'low' } },
  { id: 90280, set: { naK: 'low', cortisol: 'low', dheaS: 'low' } },
  { id: 90285, set: { naK: 'low', cortisol: 'low', dheaS: 'low', pregnenolone: 'low' } },
  { id: 90290, set: { naK: 'high', dheaS: 'high' } },
  { id: 90295, set: { naK: 'high', pregnenolone: 'high' } },
  { id: 90300, set: { cortisol: 'high', pregnenolone: 'high' } },
  { id: 90305, set: { dheaS: 'high', pregnenolone: 'high' } },
  { id: 90310, set: { naK: 'high', cortisol: 'high', dheaS: 'high' } },
  { id: 90315, set: { naK: 'high', cortisol: 'high', pregnenolone: 'high' } },
  { id: 90320, set: { naK: 'high', dheaS: 'high', pregnenolone: 'high' } },
  { id: 90325, set: { cortisol: 'high', dheaS: 'high', pregnenolone: 'high' } },
  { id: 90330, set: { naK: 'high', cortisol: 'high', dheaS: 'high', pregnenolone: 'high' } },
  { id: 90335, set: { naK: 'low', dheaS: 'low' } },
  { id: 90340, set: { naK: 'low', pregnenolone: 'low' } },
  { id: 90345, set: { cortisol: 'low', pregnenolone: 'low' } },
  { id: 90350, set: { naK: 'low', cortisol: 'low', pregnenolone: 'low' } },
  { id: 90355, set: { naK: 'low', dheaS: 'low', pregnenolone: 'low' } },
  // Cortisol dominance: high output with low reserve. The ONLY mixed-direction
  // combos defined — every other mixed set still falls back to its singles.
  { id: 90360, set: { naK: 'low',  cortisol: 'high', dheaS: 'low' } },
  { id: 90365, set: { naK: 'high', cortisol: 'high', dheaS: 'low' } },
  { id: 90370, set: { naK: 'low',  cortisol: 'high', dheaS: 'low', pregnenolone: 'low' } },
  { id: 90375, set: { naK: 'high', cortisol: 'high', dheaS: 'low', pregnenolone: 'low' } },
  /*
   * The remaining MIXED-direction two-marker states (2026-09-02). With these,
   * every two-marker state has a combo, so the one-box-per-marker fallback is
   * reached only by mixed states of three or more markers.
   *
   * Cortisol-first, then Na/K, then the reserve pair, matching both the id
   * order and the evidence order the fallback sorts by.
   */
  { id: 90680, set: { naK: 'low',  cortisol: 'high' } },
  { id: 90685, set: { naK: 'high', cortisol: 'low' } },
  { id: 90690, set: { cortisol: 'low',  dheaS: 'high' } },
  { id: 90695, set: { cortisol: 'low',  pregnenolone: 'high' } },
  { id: 90700, set: { cortisol: 'high', pregnenolone: 'low' } },
  { id: 90705, set: { naK: 'high', dheaS: 'low' } },
  { id: 90710, set: { naK: 'low',  dheaS: 'high' } },
  { id: 90715, set: { naK: 'high', pregnenolone: 'low' } },
  { id: 90720, set: { naK: 'low',  pregnenolone: 'high' } },
  { id: 90725, set: { dheaS: 'high', pregnenolone: 'low' } },
  { id: 90730, set: { dheaS: 'low',  pregnenolone: 'high' } },
];

function matchAdrenalCombo(devMap: { [k: string]: 'high' | 'low' }): number | null {
  const devKeys = Object.keys(devMap).sort().join(',');
  for (const c of ADRENAL_COMBOS) {
    if (Object.keys(c.set).sort().join(',') !== devKeys) continue;
    if (Object.keys(c.set).every(k => c.set[k] === devMap[k])) return c.id;
  }
  return null;
}

/**
 * Which Adrenal Insights pattern(s) fire for the current markers. NOTE: this is
 * independent of the gauge's single-direction logic — patterns can be mixed
 * direction (e.g. cortisol high + DHEA-S low). Rule:
 *   • exactly one marker off → its single-marker pattern;
 *   • two-or-more off matching a defined combination → that combination (which
 *     suppresses the singles it covers);
 *   • two-or-more off with no matching combination → the individual singles.
 * Cortisol AM/PM collapse to one "Cortisol" (the worse-ranked time-point).
 */
export function adrenalPatterns(
  naK?: any,
  escalators: { cortisolAM?: any; cortisolPM?: any; dheaS?: any; pregnenolone?: any } = {},
): AdrenalPattern[] {
  const { cortisolAM, cortisolPM, dheaS, pregnenolone } = escalators;

  type Dev = { key: string; dir: 'high' | 'low'; single: number; label: string };
  const devs: Dev[] = [];

  const nakDir = deviationDirection(naK);
  if (nakDir) devs.push({ key: 'naK', dir: nakDir, single: nakDir === 'high' ? 90000 : 90010, label: 'Na/K' });

  const cortisol = pickWorseCortisol(cortisolAM, cortisolPM);
  if (cortisol) devs.push({ key: 'cortisol', dir: cortisol.dir, single: cortisol.single, label: 'Cortisol' });

  const dheaDir = deviationDirection(dheaS);
  if (dheaDir) devs.push({ key: 'dheaS', dir: dheaDir, single: dheaDir === 'high' ? 90035 : 90040, label: 'DHEA-S' });

  const pregDir = deviationDirection(pregnenolone);
  if (pregDir) devs.push({ key: 'pregnenolone', dir: pregDir, single: pregDir === 'high' ? 90080 : 90090, label: 'Pregnenolone' });

  if (devs.length === 0) return [];

  const arrow = (d: Dev): AdrenalMarkerArrow => ({ direction: d.dir === 'high' ? 'up' : 'down', label: d.label });

  if (devs.length >= 2) {
    const devMap: { [k: string]: 'high' | 'low' } = {};
    for (const d of devs) devMap[d.key] = d.dir;
    const comboId = matchAdrenalCombo(devMap);
    if (comboId) {
      return [
        {
          categoryId: comboId,
          markers: devs.map(arrow),
          covers: devs.map(d => d.single),
        },
      ];
    }
  }
  /*
   * EVIDENCE ORDER for the one-box-per-marker fallback, which is where mixed
   * directions land: all 22 same-direction states have a combo, so a panel only
   * ever stacks boxes when its markers disagree.
   *
   * `devs` is built in marker-slot order (Na/K first), which printed the hedged
   * proxy box ABOVE the cortisol box that actually set the gauge — so the panel
   * opened with "Possible Lower Adrenal Output" and then contradicted it with
   * "Higher Adrenal Output" underneath. Sorted, the boxes descend in the same
   * precedence adrenalGauge uses to pick a direction: cortisol reads output
   * directly, Na/K is only an aldosterone proxy (and capped at NAK_ONLY_MAX on
   * its own), DHEA-S and pregnenolone are reserve rather than function. The
   * verdict comes first and the dissent follows it.
   *
   * The combo branch above is untouched: its single box keeps the marker row in
   * Na/K-first order, matching the gauge's own row.
   */
  const EVIDENCE_ORDER = ['cortisol', 'naK', 'dheaS', 'pregnenolone'];
  return [...devs]
    .sort((a, b) => EVIDENCE_ORDER.indexOf(a.key) - EVIDENCE_ORDER.indexOf(b.key))
    .map(d => ({ categoryId: d.single, markers: [arrow(d)] }));
}

/**
 * Maps an `adrenalPatterns()` categoryId (singles + combos) to its root-cause
 * entry, so `calculateInsights()` pushes the SAME singles/combos the Insights panel
 * renders. Built LAZILY (memoised): a module-level literal would read the
 * PossibleRootCauseCategory statics at load time, but models/index re-exports both
 * in a cycle, so the class isn't initialised yet then. Exported so a test can assert
 * the mapping is complete and id-correct.
 */
let adrenalRootCauseByIdCache: { [id: number]: PossibleRootCauseCategory } | null = null;
export function adrenalRootCauseById(): { [id: number]: PossibleRootCauseCategory } {
  if (!adrenalRootCauseByIdCache) {
    adrenalRootCauseByIdCache = {
      90000: PossibleRootCauseCategory.CATEGORY_90000,
      90010: PossibleRootCauseCategory.CATEGORY_90010,
      90015: PossibleRootCauseCategory.CATEGORY_90015,
      90020: PossibleRootCauseCategory.CATEGORY_90020,
      90025: PossibleRootCauseCategory.CATEGORY_90025,
      90030: PossibleRootCauseCategory.CATEGORY_90030,
      90035: PossibleRootCauseCategory.CATEGORY_90035,
      90040: PossibleRootCauseCategory.CATEGORY_90040,
      90080: PossibleRootCauseCategory.CATEGORY_90080,
      90090: PossibleRootCauseCategory.CATEGORY_90090,
      90240: PossibleRootCauseCategory.CATEGORY_90240,
      90245: PossibleRootCauseCategory.CATEGORY_90245,
      90250: PossibleRootCauseCategory.CATEGORY_90250,
      90255: PossibleRootCauseCategory.CATEGORY_90255,
      90260: PossibleRootCauseCategory.CATEGORY_90260,
      90265: PossibleRootCauseCategory.CATEGORY_90265,
      90270: PossibleRootCauseCategory.CATEGORY_90270,
      90275: PossibleRootCauseCategory.CATEGORY_90275,
      90280: PossibleRootCauseCategory.CATEGORY_90280,
      90285: PossibleRootCauseCategory.CATEGORY_90285,
      90290: PossibleRootCauseCategory.CATEGORY_90290,
      90295: PossibleRootCauseCategory.CATEGORY_90295,
      90300: PossibleRootCauseCategory.CATEGORY_90300,
      90305: PossibleRootCauseCategory.CATEGORY_90305,
      90310: PossibleRootCauseCategory.CATEGORY_90310,
      90315: PossibleRootCauseCategory.CATEGORY_90315,
      90320: PossibleRootCauseCategory.CATEGORY_90320,
      90325: PossibleRootCauseCategory.CATEGORY_90325,
      90330: PossibleRootCauseCategory.CATEGORY_90330,
      90335: PossibleRootCauseCategory.CATEGORY_90335,
      90340: PossibleRootCauseCategory.CATEGORY_90340,
      90345: PossibleRootCauseCategory.CATEGORY_90345,
      90350: PossibleRootCauseCategory.CATEGORY_90350,
      90355: PossibleRootCauseCategory.CATEGORY_90355,
      90360: PossibleRootCauseCategory.CATEGORY_90360,
      90365: PossibleRootCauseCategory.CATEGORY_90365,
      90370: PossibleRootCauseCategory.CATEGORY_90370,
      90375: PossibleRootCauseCategory.CATEGORY_90375,
      90680: PossibleRootCauseCategory.CATEGORY_90680,
      90685: PossibleRootCauseCategory.CATEGORY_90685,
      90690: PossibleRootCauseCategory.CATEGORY_90690,
      90695: PossibleRootCauseCategory.CATEGORY_90695,
      90700: PossibleRootCauseCategory.CATEGORY_90700,
      90705: PossibleRootCauseCategory.CATEGORY_90705,
      90710: PossibleRootCauseCategory.CATEGORY_90710,
      90715: PossibleRootCauseCategory.CATEGORY_90715,
      90720: PossibleRootCauseCategory.CATEGORY_90720,
      90725: PossibleRootCauseCategory.CATEGORY_90725,
      90730: PossibleRootCauseCategory.CATEGORY_90730,
    };
  }
  return adrenalRootCauseByIdCache;
}

export class StressAdrenalAnalysis extends Analysis {
  calculateInsights() {
    // The Adrenal Gauge row explaining the current reading (cat 904xx, Interpretation).
    // Constructed rather than taken from a PossibleRootCauseCategory static: these 177
    // rows are pure id carriers with no authored root causes, and listing them as
    // statics would add 177 enum entries plus 177 statics to two hand-maintained files
    // for no behavioural gain. Consumers only ever read `.rootCauseCategory` (see
    // further-considerations-section.tsx and tabs-component.tsx) and
    // PossibleRootCauseCategory.sort() is a plain sortBy on that field, so a
    // constructed instance behaves identically. Glucose lists its 62 gauge ids as
    // statics; this deliberately diverges because of the row count.
    const gaugeId = this.adrenalGaugeState()?.gaugeCategoryId;
    if (gaugeId != null) {
      this.possibleRootCauseCategories.push(
        new PossibleRootCauseCategory({ rootCauseCategory: gaugeId as any, possibleRootCauses: [] }),
      );
    }

    // Sodium / potassium out of optimal (either direction) → cross-reference the
    // Electrolytes section. These aren't part of the Na/K-ratio pattern logic, so
    // they're pushed directly.
    if (this.ba.sodium.isNotOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_90012);
    }
    if (this.ba.potassium.isNotOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_90014);
    }

    // Na/K ratio + Cortisol (AM/PM) + DHEA-S + Pregnenolone → the SAME singles or
    // combinations the Insights panel renders. `adrenalPatterns()` is the single
    // source of truth: one marker off → its single; 2+ matching a defined combo →
    // that combo (which suppresses the singles it covers); Cortisol AM/PM collapse
    // to one. (Markers were moved here from Electrolytes/Hormones — Na/K was
    // 9032/9034, Cortisol 15000/15010 + 15020/15030, DHEA-S 15040/15050,
    // Pregnenolone 15220/15230.)
    const patterns = adrenalPatterns(this.ba.sodiumPotassiumRatio, {
      cortisolAM: this.ba.cortisolAM,
      cortisolPM: this.ba.cortisolPM,
      dheaS: this.ba.dheaS,
      pregnenolone: this.ba.pregnenolone,
    });
    const byId = adrenalRootCauseById();
    for (const pattern of patterns) {
      const category = byId[pattern.categoryId];
      if (category) this.possibleRootCauseCategories.push(category);
      // A combination suppresses the singles it covers, which is right for the
      // Insights panel but wrong for per-marker content. Collect the covered
      // singles separately so the Causes and References tabs can show each
      // deviating marker's own rows, WITHOUT adding their Supplements to the
      // client's recommended list (see coveredRootCauseCategories).
      for (const single of pattern.covers || []) {
        const covered = byId[single];
        if (covered) this.coveredRootCauseCategories.push(covered);
      }
    }
  }

  /**
   * Single-marker categories hidden behind a combination pattern. Unioned into
   * the category list for the Causes and References tabs only — see
   * BloodworkAnalysis.possibleRootCauseCategoriesFor(section, tab). Deliberately
   * NOT part of possibleRootCauseCategories, because that list also drives the
   * Supplements tab and everything there reaches the client's Recommended
   * Supplements card ticked by default.
   */
  coveredRootCauseCategories: PossibleRootCauseCategory[] = [];

  /** Standard-sweep adrenal-axis gauge: Na/K + cortisol (the essential-function
   *  hormones) set under- vs over-activity; DHEA-S / pregnenolone read reserve when
   *  function is optimal. Null when the Na/K ratio is unmeasured. */
  adrenalGaugeState(): AdrenalGauge | null {
    return adrenalGauge(this.ba.sodiumPotassiumRatio, {
      cortisolAM: this.ba.cortisolAM,
      cortisolPM: this.ba.cortisolPM,
      dheaS: this.ba.dheaS,
      pregnenolone: this.ba.pregnenolone,
    });
  }
}
